import { yupResolver } from '@hookform/resolvers/yup';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  TextField,
  Typography,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/styles';
import _ from 'lodash';
import { useCallback, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useDropzone } from 'react-dropzone';
import { toast } from 'react-toastify';
import { mutate } from 'swr';
import * as yup from 'yup';
import { updateProviderById } from '../../../api/cms/provider';
import FormProviderMaintenace from './ProviderMaintenance';

const useStyles = makeStyles((theme) => ({
  logoWrapper: {
    display: 'inline-block',
    cursor: 'pointer',
    position: 'relative',
    '&:hover $logoOverlay': { opacity: 1 },
  },
  logo: {
    display: 'block',
    height: 160,
    width: 'auto',
    maxWidth: 320,
    borderRadius: 8,
    border: '1px solid #e0e0e0',
    objectFit: 'contain',
    background: '#fafafa',
    padding: 8,
  },
  logoOverlay: {
    position: 'absolute',
    inset: 0,
    borderRadius: 8,
    background: 'rgba(0,0,0,0.45)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    opacity: 0,
    transition: 'opacity 0.2s',
    color: '#fff',
    fontSize: 12,
    fontWeight: 600,
  },
  dropzone: {
    border: '2px dashed #bdbdbd',
    borderRadius: 8,
    padding: theme.spacing(4),
    textAlign: 'center',
    cursor: 'pointer',
    outline: 'none',
    transition: 'border-color 0.2s',
    '&:hover, &.active': { borderColor: theme.palette.primary.main },
  },
  preview: {
    marginTop: theme.spacing(2),
    textAlign: 'center',
    '& img': { maxHeight: 120, borderRadius: 6, border: '1px solid #e0e0e0' },
  },
}));

let schema = yup.object().shape({
  provider_name: yup.string().required(),
  provider_code: yup.string().required(),
  email: yup.string().email().required(),
  contact_person: yup.string().required(),
  note: yup.string(),
  phone_number: yup.string().required(),
});

const ProviderInfo = ({ providerInfo }) => {
  const classes = useStyles();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [pendingFile, setPendingFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [uploading, setUploading] = useState(false);

  const {
    control,
    handleSubmit,
    formState: { errors, isDirty, isSubmitting, isValid },
  } = useForm({
    resolver: yupResolver(schema),
    mode: 'onChange',
  });

  const onDrop = useCallback((accepted) => {
    if (!accepted.length) return;
    const file = accepted[0];
    setPendingFile(file);
    setPreviewUrl(URL.createObjectURL(file));
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: { 'image/*': [] },
    maxFiles: 1,
  });

  const openDialog = () => {
    setPendingFile(null);
    setPreviewUrl(null);
    setDialogOpen(true);
  };

  const closeDialog = () => {
    setDialogOpen(false);
    setPendingFile(null);
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setPreviewUrl(null);
  };

  const handleUpload = async () => {
    if (!pendingFile) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('provider_logo', pendingFile);
      await updateProviderById(providerInfo.provider_id, formData);
      toast.success('Logo updated');
      mutate(['providerById', providerInfo.provider_id]);
      closeDialog();
    } catch (err) {
      toast.error(_.get(err, 'response.data.message', 'Failed to upload logo'));
    } finally {
      setUploading(false);
    }
  };

  const onSubmit = (values) => {
    let formData = new FormData();
    for (const [key, value] of Object.entries(values)) {
      formData.append(key, value);
    }
    updateProviderById(providerInfo.provider_id, formData)
      .then(() => toast.success('updated'))
      .catch((error) => toast.error(_.get(error, 'response.data.message', 'error')));
  };

  return (
    <div>
      {/* Clickable logo */}
      <Box mb={2} display="flex" flexDirection="column" alignItems="center">
        <Typography variant="caption" color="textSecondary" display="block" gutterBottom>
          Provider Logo
        </Typography>
        <Box className={classes.logoWrapper} onClick={openDialog}>
          {providerInfo?.provider_logo ? (
            <img src={providerInfo.provider_logo} alt="provider logo" className={classes.logo} />
          ) : (
            <Box
              className={classes.logo}
              display="flex"
              alignItems="center"
              justifyContent="center"
              style={{ width: 320, color: '#bdbdbd', fontSize: 13 }}
            >
              No Logo
            </Box>
          )}
          <Box className={classes.logoOverlay}>
            {providerInfo?.provider_logo ? 'Change' : 'Upload'}
          </Box>
        </Box>
      </Box>

      <form onSubmit={handleSubmit(onSubmit)} style={{ marginBottom: '10px' }}>
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <Controller
              name="provider_name"
              defaultValue={providerInfo?.provider_name}
              render={({ field }) => (
                <TextField
                  variant="outlined"
                  margin="normal"
                  fullWidth
                  disabled
                  label="Provider Name"
                  error={!!errors?.provider_name}
                  {...field}
                  helperText={errors?.provider_name?.message}
                />
              )}
              control={control}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <Controller
              name="provider_code"
              defaultValue={providerInfo?.provider_code}
              render={({ field }) => (
                <TextField
                  variant="outlined"
                  margin="normal"
                  fullWidth
                  disabled
                  label="Provider Code"
                  error={!!errors?.provider_code}
                  {...field}
                  helperText={errors?.provider_code?.message}
                />
              )}
              control={control}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <Controller
              name="contact_person"
              defaultValue={providerInfo?.contact_person}
              render={({ field }) => (
                <TextField
                  variant="outlined"
                  margin="normal"
                  fullWidth
                  label="Contact Name"
                  type="text"
                  id="contact_person"
                  error={!!errors?.contact_person}
                  {...field}
                  helperText={errors?.contact_person?.message}
                />
              )}
              control={control}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <Controller
              name="phone_number"
              defaultValue={providerInfo?.phone_number}
              render={({ field }) => (
                <TextField
                  variant="outlined"
                  margin="normal"
                  fullWidth
                  label="Contact Number"
                  type="text"
                  id="phone_number"
                  error={!!errors?.phone_number}
                  {...field}
                  helperText={errors?.phone_number?.message}
                />
              )}
              control={control}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <Controller
              name="email"
              defaultValue={providerInfo?.email}
              render={({ field }) => (
                <TextField
                  variant="outlined"
                  margin="normal"
                  fullWidth
                  label="Email"
                  type="text"
                  id="email"
                  error={!!errors?.email}
                  {...field}
                  helperText={errors?.email?.message}
                />
              )}
              control={control}
            />
          </Grid>
          <Grid item xs={12}>
            <Controller
              name="royalty_fee_percent"
              defaultValue={providerInfo?.royalty_fee_percent}
              render={({ field }) => (
                <TextField
                  variant="outlined"
                  margin="normal"
                  fullWidth
                  label="Royalty Fee (%)"
                  type="text"
                  error={!!errors?.royalty_fee_percent}
                  {...field}
                  helperText={errors?.royalty_fee_percent?.message}
                />
              )}
              control={control}
            />
          </Grid>
          <Grid item xs={12}>
            <Controller
              name="note"
              defaultValue={providerInfo?.note}
              render={({ field }) => (
                <TextField
                  variant="outlined"
                  margin="normal"
                  fullWidth
                  label="Note"
                  type="text"
                  rows={3}
                  multiline
                  error={!!errors?.note}
                  {...field}
                  helperText={errors?.note?.message}
                />
              )}
              control={control}
            />
          </Grid>
          <Grid container item justifyContent="flex-end">
            <Grid>
              <Button
                type="submit"
                variant="contained"
                color="primary"
                margin="2px"
                disabled={!isDirty || isSubmitting || !isValid}
              >
                Save
              </Button>
            </Grid>
          </Grid>
        </Grid>
      </form>

      <div>
        <Grid item xs={12}>
          <FormProviderMaintenace />
        </Grid>
      </div>

      {/* Logo upload dialog */}
      <Dialog open={dialogOpen} onClose={closeDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Update Provider Logo</DialogTitle>
        <DialogContent>
          <Box
            {...getRootProps()}
            className={`${classes.dropzone}${isDragActive ? ' active' : ''}`}
          >
            <input {...getInputProps()} />
            {isDragActive ? (
              <Typography>Drop the image here…</Typography>
            ) : (
              <Typography color="textSecondary">
                Click or drag an image file here to upload
              </Typography>
            )}
          </Box>
          {previewUrl && (
            <Box className={classes.preview}>
              <img src={previewUrl} alt="preview" />
              <Typography
                variant="caption"
                display="block"
                color="textSecondary"
                style={{ marginTop: 4 }}
              >
                {pendingFile?.name}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog} disabled={uploading}>
            Cancel
          </Button>
          <Button
            onClick={handleUpload}
            color="primary"
            variant="contained"
            disabled={!pendingFile || uploading}
          >
            {uploading ? <CircularProgress size={20} color="inherit" /> : 'Upload'}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default ProviderInfo;
